import React, { useCallback, useMemo } from 'react';
import { FG141, FG142 } from '../../../services/g204Api';
import { fmt } from '../helpers';

interface Tab2SubDetailGridProps {
  details: Partial<FG141>[];
  subDetails: Partial<FG142>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  setSubDetails: React.Dispatch<React.SetStateAction<Partial<FG142>[]>>;
}

const Tab2SubDetailGrid: React.FC<Tab2SubDetailGridProps> = React.memo(({
  details, subDetails, selectedRow, isEditing,
  setSelectedRow, setSubDetails,
}) => {
  const selectedDetail = details[selectedRow];
  const parentSeq = selectedDetail?.fg141_02;

  /** Filter sub_details for the selected FG141 row */
  const filteredSubs = useMemo(() => {
    if (parentSeq == null) return [];
    return subDetails.filter(s => s.fg142_02 === parentSeq);
  }, [subDetails, parentSeq]);

  /** Add a new FG142 sub-detail row */
  const addSubDetail = useCallback(() => {
    if (parentSeq == null) return;
    const maxSub = filteredSubs.reduce((max, s) => Math.max(max, s.fg142_03 || 0), 0);
    setSubDetails(prev => [...prev, {
      fg142_01: selectedDetail?.fg141_01 || '',
      fg142_02: parentSeq,
      fg142_03: maxSub + 1,
      fg142_11: '',
      fg142_12: 0,
    }]);
  }, [parentSeq, filteredSubs, selectedDetail, setSubDetails]);

  /** Delete the selected FG142 sub-detail row */
  const [selectedSubRow, setSelectedSubRow] = React.useState(0);
  const deleteSubDetail = useCallback(() => {
    if (filteredSubs.length === 0) return;
    const toDelete = filteredSubs[selectedSubRow];
    if (!toDelete) return;
    setSubDetails(prev => {
      const next = prev.filter(s =>
        !(s.fg142_02 === toDelete.fg142_02 && s.fg142_03 === toDelete.fg142_03)
      );
      // Re-index sub-items for this parent
      let subIdx = 0;
      return next.map(s => {
        if (s.fg142_02 === parentSeq) {
          subIdx++;
          return { ...s, fg142_03: subIdx };
        }
        return s;
      });
    });
    setSelectedSubRow(Math.max(0, selectedSubRow - 1));
  }, [filteredSubs, selectedSubRow, parentSeq, setSubDetails]);

  /** Update a sub-detail field */
  const updateSubField = useCallback((subIdx: number, key: keyof FG142, value: FG142[keyof FG142]) => {
    const target = filteredSubs[subIdx];
    if (!target) return;
    setSubDetails(prev => prev.map(s => {
      if (s.fg142_02 === target.fg142_02 && s.fg142_03 === target.fg142_03) {
        return { ...s, [key]: value };
      }
      return s;
    }));
  }, [filteredSubs, setSubDetails]);

  return (
    <div>
      {/* FG141 read-only overview */}
      <div className="table-wrapper" style={{ marginBottom: 16 }}>
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13, fontWeight: 500 }}>折讓明細總覽 (FG141) - 選取列以查看出貨子明細</span>
          </div>
        </div>
        <div className="table-scroll" style={{ maxHeight: 160 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }} data-contract-field="fg141_02">項次</th>
                <th data-contract-field="fg141_11">交運單號</th>
                <th className="num" data-contract-field="fg141_13">折讓淨額</th>
                <th className="num" data-contract-field="fg141_14">稅額</th>
                <th data-contract-field="fg141_16">幣別</th>
                <th className="num" data-contract-field="fg141_17">折讓金額</th>
                <th data-contract-field="fg141_18">客訴單號</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, i) => (
                <tr
                  key={i}
                  className={selectedRow === i ? 'selected' : ''}
                  onClick={() => setSelectedRow(i)}
                >
                  <td>{d.fg141_02}</td>
                  <td>{d.fg141_11 || ''}</td>
                  <td className="num">{fmt(d.fg141_13)}</td>
                  <td className="num">{fmt(d.fg141_14)}</td>
                  <td>{d.fg141_16 || ''}</td>
                  <td className="num">{fmt(d.fg141_17)}</td>
                  <td>{d.fg141_18 || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FG142 sub-detail editable grid */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              出貨子明細 (FG142) - 項次 {parentSeq ?? '-'}
            </span>
          </div>
          <div className="right">
            <button
              className="btn btn-sm"
              disabled={!isEditing || parentSeq == null}
              onClick={addSubDetail}
            >
              新增出貨
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={!isEditing || filteredSubs.length === 0}
              onClick={deleteSubDetail}
            >
              刪除出貨
            </button>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }} data-contract-field="fg142_03">子項次</th>
                <th data-contract-field="fg142_11">出貨單號</th>
                <th className="num" data-contract-field="fg142_12">折讓淨額</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubs.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                    {parentSeq == null ? '請先選取明細列' : '無出貨子明細'}
                  </td>
                </tr>
              )}
              {filteredSubs.map((s, i) => (
                <tr
                  key={i}
                  className={selectedSubRow === i ? 'selected' : ''}
                  onClick={() => setSelectedSubRow(i)}
                >
                  <td>{s.fg142_03}</td>
                  <td>
                    <input
                      type="text"
                      value={s.fg142_11 || ''}
                      onChange={e => updateSubField(i, 'fg142_11', e.target.value)}
                      disabled={!isEditing}
                      style={{ width: 120 }}
                    />
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      value={s.fg142_12 ?? ''}
                      onChange={e => updateSubField(i, 'fg142_12', parseFloat(e.target.value) || 0)}
                      disabled={!isEditing}
                      style={{ width: 120, textAlign: 'right' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

Tab2SubDetailGrid.displayName = 'Tab2SubDetailGrid';
export default Tab2SubDetailGrid;
